import { SDUIOnClickHandler } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import {
  SDUIImageElementRenderer,
  ISDUIImageElementRendererProps,
} from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Image/SDUIImageElementRenderer";
import { SDUITileInstanceRendererProps } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/types";
import { SDUITypographyRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/SDUITypographyRenderer";
import { cn } from "@faire/web--source/tools/cn";
import { IImageElement } from "@faire/web-api--source/indigofair/data/IImageElement";
import { IImageFanTile } from "@faire/web-api--source/indigofair/data/IImageFanTile";
import React from "react";

// fallback
const DEFAULT_IMAGE_TILE_OPTIMIZE_WIDTH = 250;

export const SDUIImageFanTileRenderer = ({
  tileElement,
  loggingData,
  renderBehaviour,
}: SDUITileInstanceRendererProps) => {
  const { on_click, image_fan_tile } = tileElement;
  if (!image_fan_tile) {
    return null;
  }

  const { primary_text, image_elements, image_fan_tile_style, subtitle_text } =
    image_fan_tile;
  if (!image_elements[0] || !primary_text || !image_fan_tile_style) {
    return null;
  }
  const { animation_on_hover } = image_fan_tile_style;
  const hasHighlightBorderAnimation = animation_on_hover?.includes(
    IImageFanTile.ImageFanTileAnimation.HIGHLIGHT_BORDER
  );
  const hasFanOutImagesAnimation = animation_on_hover?.includes(
    IImageFanTile.ImageFanTileAnimation.FAN_OUT_IMAGES
  );

  return (
    <SDUIOnClickHandler
      onClickActions={on_click}
      loggingData={loggingData}
      logLegacyClickEvent
      componentStyle={{
        outline: "revert",
      }}
    >
      <div
        className={cn(
          // minimum height is a result of 16px padding and 1px border on top and bottom, plus 58px maximum in the case of 2 lines of primary_text and one line of subtitle_text
          "group border-core-border-muted flex min-h-[92px] flex-row gap-3 overflow-hidden rounded-sm border border-solid p-4 lg:flex-row-reverse lg:justify-end lg:gap-5",
          hasHighlightBorderAnimation ? "hover:border-core-surface-hover" : null
        )}
      >
        <div className="flex shrink-0 basis-[calc(100%-32px)] flex-col justify-end gap-0.5 lg:shrink-1 lg:basis-auto lg:justify-center">
          <SDUITypographyRenderer
            textElement={primary_text}
            loggingData={loggingData}
          />
          {subtitle_text ? (
            <SDUITypographyRenderer
              textElement={subtitle_text}
              loggingData={loggingData}
            />
          ) : null}
        </div>
        <div className="relative flex flex-row lg:basis-20">
          <div
            className={cn(
              "z-3 grid -rotate-10 overflow-hidden rounded-sm transition duration-300 lg:rotate-0",
              hasFanOutImagesAnimation ? "group-hover:-rotate-3" : null,
              "[--fan-tile-width:56px] xl:[--fan-tile-width:64px]"
            )}
          >
            <div className="image-overlay z-1 col-[1] row-[1] h-full w-full bg-[#00000005]" />
            <SDUIImageElementRenderer
              className="col-[1] row-[1]"
              {...getImageElementProps({
                imageElement: image_elements[0],
                isFrontImage: true,
                loggingData,
                renderBehaviour,
              })}
            />
          </div>
          {image_elements[1] ? (
            <div
              className={cn(
                "z-2 -ml-[28px] grid rotate-14 transform self-center overflow-hidden rounded-sm transition duration-300",
                hasFanOutImagesAnimation
                  ? "group-hover:translate-x-[8px] group-hover:translate-y-[1px] group-hover:rotate-22"
                  : null,
                "[--fan-tile-width:44px] xl:[--fan-tile-width:52px]"
              )}
            >
              <div className="image-overlay z-1 col-[1] row-[1] h-full w-full bg-[#0000000d]" />
              <SDUIImageElementRenderer
                className="col-[1] row-[1]"
                {...getImageElementProps({
                  imageElement: image_elements[1],
                  isFrontImage: false,
                  loggingData,
                  renderBehaviour,
                })}
              />
            </div>
          ) : null}
        </div>
      </div>
    </SDUIOnClickHandler>
  );
};

const getImageElementProps = ({
  imageElement,
  isFrontImage,
  ...restTileRendererProps
}: {
  imageElement: IImageElement;
  isFrontImage: boolean;
} & Pick<
  SDUITileInstanceRendererProps,
  "loggingData" | "renderBehaviour"
>): ISDUIImageElementRendererProps => {
  return {
    ...restTileRendererProps,
    imageElement,
    imageStyles: {
      aspectRatio: 1,
      width: "var(--fan-tile-width)",
      height: "auto",
      isCornersRounded: true,
    },
    optimizeImageOptions: {
      resizeMethod: "fill",
      width: imageElement.image?.width ?? DEFAULT_IMAGE_TILE_OPTIMIZE_WIDTH,
      height: imageElement.image?.height ?? DEFAULT_IMAGE_TILE_OPTIMIZE_WIDTH,
    },
  };
};
